import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import CancelInvoiceDialog from './CancelInvoiceDialog';
import InvoiceOptionsDialog, { type InvoiceOptionsMode } from './InvoiceOptionsDialog';
import { createInvoices, previewInvoiceCreation, requestEdiSend, INVOICE_CLASS_OPTIONS, type EdiSendAction, type HeldInvoice, type InvoiceClass } from '../../services/accountingInvoiceSelection';
import ProMarginModal from '../../components/pro/ProMarginModal';
import { useProMargin } from '../../hooks/useProMargin';
import { openProSearch } from '../../utils/proSearch';
import { money } from '../../services/pendingEdiInvoices.service';
import AccessorialEditModal from '../pro/components/AccessorialEditModal';
import QuoteLaneEditorDrawer from '../order-management/components/QuoteLaneEditorDrawer';
import EdiInfoModal from '../pro/components/EdiInfoModal';
import { type QuoteListRow } from '../order-management/fixtures';
import { openQuotePdf } from '../../utils/quotePdf';
import { gwiCommissionFor } from '../../utils/gwiCommission';
import {
  InvoiceHold,
  InvoiceSkip,
  holdApplies,
  InvoiceListAccessorial,
  InvoiceListDriverPay,
  InvoiceListFlag,
  InvoiceListLeg,
  InvoiceListParams,
  InvoiceListProMargin,
  InvoiceListResponse,
  InvoiceListRow,
  marginBatches,
  getInvoiceList,
  getMarginHolds,
  getSelectionHolds,
  invoiceListPath,
} from '../../services/invoiceList.service';

/**
 * Invoice List (Accounting)
 * -------------------------
 * V1's `lib_invoice.pl::listinvoice` — one invoice with its orders, its legs and its
 * charges, or the orders under a masterpro that have not been invoiced yet.
 *
 * ENTERED FROM SEVERAL PLACES WITH SEVERAL DIFFERENT IDENTITIES, and every one is a URL
 * query. The EDI Pending Invoices screen links with `?invhistoryrecno=`; a pro screen
 * links with `?masterpro=`; a search box links with a bill-to name and a date range.
 * Nothing here decides which entry point it is — the server resolves the tuple and
 * answers with one of four statuses. `ambiguous` is V1's picker, not an error.
 *
 * WHY THIS SCREEN IS COLOURED THE WAY IT IS
 * -----------------------------------------
 * The highlighting is V1's, and it is INFORMATION rather than decoration. This is a
 * comparison screen: someone checks the charges against what actually happened on the
 * move, and the colour is what lets them do that without reading every field. Four
 * rules, all from lib_invoice.pl:
 *
 *   yellow (`flag`)  the four reference numbers a biller matches against the customer's
 *                    paperwork — equipment, BOL, bill-to ref, vessel (`$yellowbg`,
 *                    :1602) — plus the two dates the charges are computed from
 *                    (:1514, always highlighted), plus any order flag set to Y
 *                    (:1417/:1454), plus the invoice total (:1060).
 *   red (`alert`)    COD, credit hold, a cancelled invoice, a charge with no amount.
 *                    Money that will not arrive the usual way.
 *   banded row       the DELIVERY leg (:1318) — the one the customer is being billed
 *                    for, shown with its full address underneath.
 *   inverted chip    invoice date and Sage export date, filled only once they have
 *                    happened, so "has this gone out" is answerable at a glance (:1379).
 *
 * The yellow is `HL`, taken verbatim from the Pro Details flag fields
 * (pages/pro/components/orderform/FlagInput.tsx) — the Scale/OTR treatment. One
 * highlight vocabulary across the app.
 */

/**
 * The Pro Details highlight, verbatim: a BRIGHT fill that is the same in both themes,
 * with black text forced on top of it.
 *
 * The `dark:` half repeating the light value is the point, not a mistake — this mark
 * means "look at this", and a highlight that dims itself in dark mode has stopped doing
 * its job. Because the fill does not change, the text colour must not either, which is
 * why `text-black` rides along and why every call site lets HL own the colour rather
 * than pairing it with `text-body`.
 */
const HL = 'bg-yellow-300 dark:bg-yellow-300 text-black';

/** Its red counterpart, from pages/pro/components/fields.tsx — a softer wash, because
 *  red is already loud enough without a bright fill. */
const HL_RED = 'bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-200 font-semibold';

/** An orange wash for the accessorial banner. Its own tone on purpose: yellow (HL)
 *  marks a FIELD worth reading and red (HL_RED) marks a problem, while this marks
 *  what KIND of record you are looking at — a third statement that should not be
 *  confused with either. Matches the orange V1 paints the same bar. */
const HL_ACCESSORIAL =
  'bg-orange-200 dark:bg-orange-900/50 text-orange-950 dark:text-orange-100';

/** V1's three EDI control labels, verbatim (lib_invoice.pl:1229, :1246, :1249) — the
 *  words Accounting reads this screen by. `EDI not sent, Send EDI` is V1's phrasing for
 *  lifting a do-not-send: it states the current fact, then offers the action. */
/** What the picker says separates its candidates. `invoicenum` and `masterpro` are the
 *  same COLUMN — `COALESCE(h.masterpro, o.masterpro)` — reading as the invoice number
 *  once an invoice exists and the order's masterpro before that. Searching by order
 *  masterpro and being told the results "differ by masterpro" names the one thing they
 *  all share. */
const PICKFIELD_LABELS: Record<string, string> = {
  billto: 'bill-to',
  invoicenum: 'invoice number',
  masterpro: 'masterpro',
};

const EDI_ACTION_LABELS: Record<string, string> = {
  resend: 'Resend EDI',
  cancel_resend: 'Cancel Resend EDI',
  send: 'EDI not sent, Send EDI',
};

const EDI_ACTION_TITLES: Record<string, string> = {
  resend: 'Flag this invoice for the EDI script to send again on its next run',
  cancel_resend: 'Withdraw the resend request before the EDI script picks it up',
  send: 'Lift the do-not-send flag so EDI sends this invoice',
};

const HOLD_HELP: Record<string, string> = {
  not_approved: 'The order has not been approved for invoicing.',
  pending_approval: 'The order is waiting on an approval.',
  trouble_ticket: 'An open trouble ticket in category REQUIRED FOR INVOICING.',
  missing_driver_rates: 'A driver pay line or leg has no rate.',
  missing_invoice_amount: 'A charge is on the invoice with no amount.',
  missing_required_docs: 'Paperwork the bill-to requires has not been received.',
  margin_approval: 'The margin is below the limit for this order.',
  edi_errors: 'This invoice would be rejected if sent over EDI.',
  mas_export: 'The invoice cannot be exported to Sage as it stands.',
  // Home Depot family only — the two gates that came off the retired CMS Home Depot
  // invoicing screen (#33785). See backend holds.HD_INVOICING_BILLTOS.
  missing_outgate: 'The container has no outgate date/time, which Home Depot requires.',
  missing_fuel_index: 'No published fuel index covers the week this order delivered.',
  quote_invalid:
    'The quote on this invoice is deleted, missing, or expired before the date its rate '
    + 'applies. Move the pro to a quote that covers this move.',
};

const dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : `${v}`;

const dateOnly = (v: string | null) => (v ? v.slice(0, 10) : '');
const dateTime = (v: string | null) => (v ? v.replace('T', ' ').slice(0, 16) : '');

/** Two values V1 prints side by side in one cell (size + type, lbs + kgs, vsl + voy). */
const pair = (a: string, b: string) => [a, b].filter(Boolean).join('  ') || '—';

/* ---------------------------------------------------------------- atoms */

/** A label/value pair from V1's left column. `highlight` is the `$yellowbg` set; `alert`
 *  is the red one — money that will not arrive the usual way. */
const Row: React.FC<{
  label: string; value?: React.ReactNode; highlight?: boolean; alert?: boolean;
}> = ({ label, value, highlight, alert }) => (
  <div className="flex items-baseline gap-2 px-2 py-[3px] odd:bg-black/[0.02] dark:odd:bg-white/[0.02]">
    <span className="w-28 shrink-0 text-right text-[11px] text-muted">{label}</span>
    <span
      className={`min-w-0 flex-1 truncate text-[13px] ${
        alert ? `rounded-sm px-1 ${HL_RED}`
          : highlight ? `rounded-sm px-1 font-medium ${HL}` : 'text-body'
      }`}
    >
      {value ?? '—'}
    </span>
  </div>
);

/**
 * A header bar. `pro` is the LEGS grid's exact dark bar (LegsGrid.tsx:4273) so this
 * screen and the Pro Details screen read as the same system; `section` is one step
 * lighter, which is what separates "this is a pro" from "this is a table inside it".
 * Legacy paints both navy — the two tones are what the flat version was missing.
 */
const Bar: React.FC<{
  children: React.ReactNode;
  tone?: 'pro' | 'section';
  className?: string;
}> = ({ children, tone = 'pro', className = '' }) => (
  <div
    className={`flex items-center gap-2 px-3 py-1.5 text-[0.78125rem] font-semibold uppercase tracking-wider text-white ${
      tone === 'pro' ? 'bg-slate-800 dark:bg-gray-800' : 'bg-slate-700 dark:bg-gray-700'
    } ${className}`}
  >
    {children}
  </div>
);

/** Invoice date / Sage export date. Inverted once set — "this has actually happened". */
const Stamp: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <span
    className={`inline-flex items-baseline gap-1 rounded px-1.5 py-0.5 text-[12px] tabular-nums ${
      value ? 'bg-heading font-semibold text-panel' : 'text-muted'
    }`}
    title={value ? undefined : `Not ${label === 'I:' ? 'invoiced' : 'exported to Sage'} yet`}
  >
    <span className="font-semibold">{label}</span>
    {value ? dateOnly(value) : '—'}
  </span>
);

const Flag: React.FC<{ flag: InvoiceListFlag }> = ({ flag }) => {
  const tone =
    flag.emphasis === 'alert'
      ? HL_RED
      : flag.emphasis === 'flag'
        ? `${HL} font-medium`
        : 'text-muted';
  return (
    <div className={`flex items-baseline justify-between gap-2 rounded-sm px-1.5 py-[2px] ${tone}`}>
      <span className="text-[11px]">{flag.label}</span>
      <span className="text-[12px] tabular-nums">{flag.value || '—'}</span>
    </div>
  );
};

const HoldBanner: React.FC<{ hold: InvoiceHold }> = ({ hold }) => (
  <div className="rounded border-l-4 border-danger-fg bg-danger px-3 py-2">
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-sm font-bold text-danger-fg">{hold.label}</span>
      {hold.v1Silent && (
        <span
          className="text-[11px] text-danger-fg/80"
          title="V1 computes this check but never lets it block, so an invoice where this is the only problem passes there unremarked."
        >
          (not enforced in V1)
        </span>
      )}
      {HOLD_HELP[hold.code] && (
        <span className="text-xs text-danger-fg/80">{HOLD_HELP[hold.code]}</span>
      )}
    </div>
    {hold.detail.length > 0 && (
      <ul className="mt-1 space-y-0.5 text-xs text-danger-fg/90">
        {hold.detail.map((d) => <li key={d}>{d}</li>)}
      </ul>
    )}
    {hold.pronumbers.length > 0 && (
      <p className="mt-1 text-[11px] tabular-nums text-danger-fg/70">
        Pro {hold.pronumbers.join(', ')}
      </p>
    )}
  </div>
);

/* ----------------------------------------------------------- leg strip */

const LegStrip: React.FC<{ legs: InvoiceListLeg[] }> = ({ legs }) => {
  if (!legs.length) {
    return <p className="px-3 py-2 text-xs text-muted">No legs on this order.</p>;
  }
  return (
    <table className="w-full text-[12px]">
      <thead>
        <tr className="bg-slate-700 text-[10px] uppercase tracking-wider text-white dark:bg-gray-700">
          <th className="w-10 px-2 py-1 text-right font-semibold">&nbsp;</th>
          <th className="px-2 py-1 text-left font-semibold">Location</th>
          <th className="w-12 px-1 py-1 text-center font-semibold">Drop</th>
          <th className="w-12 px-1 py-1 text-center font-semibold">Mule</th>
          <th className="w-16 px-1 py-1 text-left font-semibold">Driver</th>
          <th className="w-24 px-2 py-1 text-left font-semibold">Date</th>
        </tr>
      </thead>
      <tbody>
        {legs.map((leg) => (
          <React.Fragment key={leg.legnum}>
            {/* Leg 1 carries an extra leading row for the ORIGIN, so the strip starts at
                the pickup rather than at the first drop. */}
            {leg.originCode && (
              <tr className="border-b border-hairline">
                <td className="px-2 py-1 text-right font-semibold text-muted">
                  {leg.originCode}:
                </td>
                {/* The pickup date sits in the DATE column like every row below it. V1
                    spans this row across all six columns and prints no date at all —
                    it is nearly always the same day as the first destination, but an
                    empty cell in a column of dates reads as unknown rather than same. */}
                <td className="px-2 py-1 text-body" colSpan={4}>{leg.originName}</td>
                <td className="px-2 py-1 tabular-nums text-body">{dateOnly(leg.originDate)}</td>
              </tr>
            )}
            <tr
              className={`border-b border-hairline ${
                leg.isDelivery ? 'bg-sunken font-medium' : ''
              }`}
            >
              <td className="px-2 py-1 text-right font-semibold text-muted">{leg.code}:</td>
              <td className="px-2 py-1 text-body">{leg.location || '—'}</td>
              <td className="px-1 py-1 text-center text-body">{leg.drop}</td>
              <td className="px-1 py-1 text-center text-body">{leg.mule}</td>
              <td className="px-1 py-1 tabular-nums text-body">{leg.driver}</td>
              <td className="px-2 py-1 tabular-nums text-body">{dateOnly(leg.date)}</td>
            </tr>
            {leg.isDelivery && (
              <tr className="border-b border-hairline bg-sunken">
                <td className="px-2 py-1 text-right text-[11px] text-muted">Addr:</td>
                <td className="px-2 py-1 text-body" colSpan={5}>
                  {[leg.address, leg.city, leg.stateZip].filter(Boolean).join(', ') || '—'}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

/* ------------------------------------------------------- driver pay bar */

/**
 * V1's DRIVER PAY strip (lib_invoice.pl:1120) — the pay total and the margin, and a
 * click through to the full breakdown.
 *
 * ALWAYS RENDERED, including on a pro with no driver pay recorded: "no pay on this
 * move" is itself something the biller needs to see next to the charges, and a bar that
 * disappears when the number is zero cannot say it. V1 paints it unconditionally too.
 *
 * The numbers arrive with the deferred margin fetch rather than with the page, because
 * they are the same rate-engine pass that produces the MISSING DRIVER RATES and MARGIN
 * APPROVAL holds — computing them twice would double the slowest thing on the screen.
 */
const DriverPayBar: React.FC<{
  margin: InvoiceListProMargin | undefined;
  pending: boolean;
  onOpen: () => void;
}> = ({ margin, pending, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    disabled={!margin}
    className={`flex w-full items-center gap-3 border-t border-hairline px-3 py-1.5 text-left text-[0.78125rem] font-semibold uppercase tracking-wider text-white ${
      margin
        ? 'cursor-pointer bg-slate-700 hover:bg-slate-600 dark:bg-gray-700 dark:hover:bg-gray-600'
        : 'cursor-default bg-slate-700/70 dark:bg-gray-700/70'
    }`}
    title={margin ? 'Show the customer pay / driver pay breakdown' : undefined}
  >
    <span>Driver Pay</span>
    {pending && !margin && <span className="font-normal normal-case opacity-70">calculating…</span>}
    {margin && (
      <>
        <span className="tabular-nums">{money(margin.driverPay)}</span>
        <span className="font-normal normal-case opacity-70">Margin</span>
        <span
          className={`rounded px-1.5 tabular-nums ${
            margin.underLimit ? 'bg-brand-red text-white' : ''
          }`}
          title={margin.underLimit
            ? `Below the ${margin.marginLimit}% limit for this order`
            : `Limit ${margin.marginLimit}%`}
        >
          {margin.marginPct.toFixed(2)}%
        </span>
        <span className="ml-auto font-normal normal-case opacity-70">Show breakdown →</span>
      </>
    )}
  </button>
);

/* ----------------------------------------------------- accessorials strip */

/**
 * V1's ACCESSORIALS panel (lib_invoice.pl:1199) — every live accessorial on the pro,
 * under its charges.
 *
 * It is not a view of this invoice. It is a view of the PRO, and that is the point: an
 * accessorial already billed on its own invoice is invisible in the charge table yet is
 * exactly what a biller needs to know about before raising another one. Masterpro
 * 5108048 is the case that prompted it — $479.33 of accessorial already invoiced behind
 * a $1,714.33 base invoice, with nothing on the screen to say so.
 *
 * Colour is V1's, and carries the state: red = invoiced, black = exported to Sage.
 * Exported wins, because V1 assigns red and then overwrites it (:1208-1209).
 *
 * An open accessorial that is ALSO on this page — billed on this invoice, its own block
 * further down under the orange ACCESSORIAL bar — takes that same orange here, so a
 * biller can tell from the top of the pro whether there is an accessorial below to
 * scroll down to (billers' request). Red and black still win: they are V1's billing
 * state, and an accessorial on an uninvoiced page is open anyway. An open accessorial
 * NOT on this page (billed to someone else) stays unpainted.
 *
 * Every row opens the accessorial itself. The modal is the SAME component the order's
 * Invoicing tab opens, so the record's own locks come with it — an invoiced accessorial
 * is read-only here exactly as it is there.
 */
const ACC_STATE_CLASS: Record<InvoiceListAccessorial['state'], string> = {
  // bg-brand-red is V1's $colors{'red'}; black is literal in V1 and stays literal here
  // rather than becoming a theme token, because "exported to Sage" is a terminal state
  // and reads as one.
  exported: 'bg-black text-white hover:bg-gray-800',
  invoiced: 'bg-brand-red text-white hover:bg-red-700',
  open: 'bg-panel text-body hover:bg-subtle',
};

const ACC_STATE_TITLE: Record<InvoiceListAccessorial['state'], string> = {
  exported: 'Exported to Sage — open the accessorial (read-only)',
  invoiced: 'Already invoiced — open the accessorial (read-only)',
  open: 'Not yet invoiced — open the accessorial',
};

const AccessorialsStrip: React.FC<{
  /** Optional rather than required: a cached bundle can outlive a deploy and meet an API
   *  that does not send the field yet, and the whole page should not blank out over it. */
  accessorials: InvoiceListAccessorial[] | undefined;
  onOpen: (pro: number, recno: number) => void;
  /** `invoice.recno`s with a block of their own on this page. */
  pageRecnos?: ReadonlySet<number>;
}> = ({ accessorials, onOpen, pageRecnos }) => {
  if (!accessorials?.length) return null;
  return (
    // mt-4 separates this from the Driver Pay bar above: two dark bars flush against each
    // other read as one control, and at mt-2 they still crowded. V1 spaces them the same
    // way, with an empty spacer row before the header (lib_invoice.pl:1201).
    <div className="mt-4 border-t border-hairline">
      <div className="bg-slate-700 px-3 py-1 text-center text-[0.78125rem] font-semibold uppercase tracking-wider text-white dark:bg-gray-700">
        Accessorials
      </div>
      {accessorials.map((acc) => {
        const below = acc.state === 'open' && !!pageRecnos?.has(acc.invoicerecno);
        return (
          <button
            key={acc.invoicerecno}
            type="button"
            onClick={() => onOpen(acc.pronumber, acc.invoicerecno)}
            title={below
              ? 'Not yet invoiced — on this invoice, further down the page. Open the accessorial'
              : ACC_STATE_TITLE[acc.state]}
            className={`flex w-full items-center justify-center gap-2 border-t border-hairline px-3 py-1 text-center text-[12px] ${
              below
                ? `${HL_ACCESSORIAL} hover:bg-orange-300 dark:hover:bg-orange-900/70`
                : ACC_STATE_CLASS[acc.state]
            }`}
          >
            <span>{acc.accessorialnum}. {acc.billtoname || '—'}</span>
            <span className="underline tabular-nums">{money(acc.netTotal)}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * The pro's ADDITIONAL driver pay, beside the charges it should be billed against.
 *
 * Clerks were opening the Addl Cost tab for exactly this comparison: every one of these
 * is money going out, and the question is whether anything on the invoice bills it back.
 * Leg pay is deliberately not here — that is the move itself, and the Driver Pay bar
 * below already answers it.
 *
 * COMPACT BY DESIGN. The pro's table is nine columns wide and this sits in the narrowest
 * of three, so each item is one row: the identity (date, driver, type) reads left, the
 * money reads right, and rate / commission / fuel — the three that make up the net — are
 * a second muted line only when they have something to say. A pro with six additions
 * costs six rows here, not six table rows plus a header.
 *
 * Commission and net come from `gwiCommissionFor`, the same helper the Addl Cost tab
 * calls, rather than being recomputed — the 30% and its interaction with fuel live in one
 * place.
 */
const DriverPayAdditions: React.FC<{
  /** Optional rather than required: a cached bundle can outlive a deploy and meet an API
   *  that does not send the field yet, and the whole page should not blank out over it. */
  items: InvoiceListDriverPay[] | undefined;
}> = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted">Addl driver pay</p>
      <div className="mt-[2px] divide-y divide-hairline rounded bg-sunken">
        {items.map((it) => {
          const { commission, net } = gwiCommissionFor(it.rate, it.gwiCommission, it.fuelAmount);
          // Only the parts that carry information: commission is absent on a non-commission
          // driver and fuel is 0 on most additions, and printing "-0.00 / 0.00" under every
          // row is the clutter this layout exists to avoid.
          const parts = [
            it.rate != null ? `rate ${money(it.rate)}` : null,
            commission ? `comm -${money(commission)}` : null,
            it.fuelAmount ? `fuel ${money(it.fuelAmount)}` : null,
            it.payrollDate ? `pr ${it.payrollDate}` : null,
          ].filter(Boolean);
          return (
            <div key={it.recno} className="px-2 py-[3px] text-[12px]">
              <div className="flex items-baseline gap-1.5">
                <span className="shrink-0 tabular-nums text-muted">{it.eventDate || '—'}</span>
                <span className="shrink-0 tabular-nums text-muted">{it.driver || '—'}</span>
                <span className="min-w-0 flex-1 truncate font-medium text-body" title={it.type}>
                  {it.type || '—'}
                  {/* V1 and the pro both name the pro a cross-pro addition was billed to;
                      on this screen that is the difference between pay that belongs to
                      this invoice and pay that belongs to somebody else's. */}
                  {it.lineItemPro && <span className="text-muted"> ({it.lineItemPro})</span>}
                </span>
                <span className="shrink-0 tabular-nums font-semibold text-body">
                  {net != null ? money(net) : '—'}
                </span>
                {it.payStatus && (
                  <span className="shrink-0 text-[11px] uppercase text-muted">{it.payStatus}</span>
                )}
              </div>
              {parts.length > 0 && (
                <div className="truncate text-[11px] text-muted">{parts.join(' · ')}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * The quote this invoice was priced from, above the charges it priced.
 *
 * V1's listinvoice never shows this — it selects `quotenumber` only to feed a Walmart
 * validation check (lib_invoice.pl:2155) and prints it nowhere. It is here because this
 * is a comparison screen: a biller checking charges against the move has, until now, had
 * to leave the page to see what the customer was actually quoted.
 *
 * Placed above the charge table rather than in the field list on the left, for the same
 * reason the order's Invoicing tab puts it above Quote Charges: it is the SOURCE of what
 * is underneath it, not another reference number.
 *
 * Both halves must be non-zero to open anything. A quote number with revision 0 names no
 * `quoterevisions` row, so the drawer would load nothing and the PDF would render
 * nothing; those invoices show the bare number and say why.
 */
const QuoteRef: React.FC<{
  quoteNumber?: number;
  revisionNumber?: number;
  fromMain?: boolean;
  onOpen: (quoteNumber: number, revisionNumber: number) => void;
}> = ({ quoteNumber, revisionNumber, fromMain = false, onOpen }) => {
  // Local to the one line that can fail: a PDF that will not render is this control's
  // problem, and routing it to the page-level banner would push the holds off screen.
  const [pdfFailed, setPdfFailed] = useState(false);
  const linkable = !!quoteNumber && !!revisionNumber;
  const quoteId = `${quoteNumber}-${revisionNumber}`;
  return (
    <div className="flex items-center gap-2 border-b border-hairline px-3 py-1">
      <span className="text-[11px] uppercase tracking-wide text-muted">Quote</span>
      {linkable ? (
        <>
          <button
            type="button"
            onClick={() => onOpen(quoteNumber!, revisionNumber!)}
            className="text-[12px] font-medium tabular-nums text-accent-text hover:underline"
            title="Open this quote in a drawer"
          >
            {quoteId}
          </button>
          <button
            type="button"
            onClick={() => {
              setPdfFailed(false);
              void openQuotePdf(quoteNumber!, revisionNumber!).then((ok) => setPdfFailed(!ok));
            }}
            className="text-muted hover:text-body"
            title="Open the quote PDF"
            aria-label={`Open the PDF for quote ${quoteId}`}
          >
            <FileText className="h-3.5 w-3.5" aria-hidden />
          </button>
          {/* Said out loud, not left to be inferred. Most accessorials carry no quote of
              their own and bill under the main invoice's, so this is the common case on
              an accessorial block — and a borrowed quote presented as the invoice's own
              would have a biller chasing a link the record does not actually hold. */}
          {fromMain && (
            <span className="text-[11px] text-muted" title="This accessorial has no quote of its own, so it bills under the main invoice's — the same quote the invoice estimate prices its charges from.">
              from the main invoice
            </span>
          )}
          {pdfFailed && (
            <span className="text-[11px] text-danger-fg">PDF could not be rendered.</span>
          )}
        </>
      ) : (
        <span className="text-[12px] text-muted" title={quoteNumber
          ? 'This invoice names a quote but no revision, so there is no revision to open.'
          : 'No quote is linked to this invoice.'}>
          {quoteNumber ? `${quoteNumber} (no revision)` : '—'}
        </span>
      )}
    </div>
  );
};

/**
 * The margin breakdown, fetched only when someone asks for it.
 *
 * Mounted per selected pro rather than per row: `useProMargin` fires a request on mount,
 * and one per pro on page load would reintroduce exactly the cost the deferred fetch
 * exists to avoid.
 */
const MarginBreakdown: React.FC<{ pro: number; onClose: () => void }> = ({ pro, onClose }) => {
  const { margin, loading } = useProMargin(pro, true);
  if (!margin) return null;
  return <ProMarginModal open onClose={onClose} margin={margin} loading={loading} />;
};

/* ------------------------------------------------------------ pro block */

const ProBlock: React.FC<{
  row: InvoiceListRow;
  index: number;
  count: number;
  sageTag: string;
  billtoname: string;
  billto: number;
  credithold: string;
  margin: InvoiceListProMargin | undefined;
  marginPending: boolean;
  /** The holds that name THIS pro. Every hold carries `pronumbers`; the block used to
   *  render only the required-document list, so a pro held for anything else — PENDING
   *  APPROVAL, a trouble ticket, a thin margin — showed a banner at the top of the page
   *  and nothing at all on the block you were reading. */
  holds: InvoiceHold[];
  onOpenPro: (pro: number) => void;
  onOpenMargin: (pro: number) => void;
  onOpenAccessorial: (pro: number, recno: number) => void;
  onOpenQuote: (quoteNumber: number, revisionNumber: number) => void;
  onOpenEdi: (pro: number) => void;
  /** V1 offers "Remove from this invoice" on an uninvoiced ACCESSORIAL when the page
   *  holds more than one row (lib_invoice.pl:926) — an accessorial run is assembled from
   *  whatever happens to be outstanding, and one of them is often not ready to bill. */
  removable?: boolean;
  excluded?: boolean;
  onToggleExcluded?: () => void;
  /** Every `invoice.recno` with a block on this page — the Accessorials strip marks the
   *  ones you will find further down. */
  pageRecnos?: ReadonlySet<number>;
}> = ({
  row, index, count, sageTag, billtoname, billto, credithold,
  margin, marginPending, holds, onOpenPro, onOpenMargin, onOpenAccessorial, onOpenQuote,
  onOpenEdi,
  removable = false, excluded = false, onToggleExcluded, pageRecnos,
}) => (
  <div className={`overflow-hidden rounded-lg border bg-panel ${
    excluded ? 'border-dashed border-muted opacity-60' : 'border-rule'}`}>
    <Bar className="flex-wrap">
      <button
        type="button"
        onClick={() => onOpenPro(row.pronumber)}
        className="tabular-nums underline-offset-2 hover:underline"
        title="Open the order"
      >
        Pro {row.pronumber}
      </button>
      <span className="opacity-70">{index + 1} of {count}</span>
      <span className="opacity-50">|</span>
      <span>Master Pro {row.masterpro}</span>
      <span className="opacity-50">|</span>
      <span>Sage {sageTag || '—'}</span>
      <span className="opacity-50">|</span>
      <span>{row.movetype || '—'}</span>
      {/* Same link, same label and same modal as the pro's Addl Info tab — a biller
          checking charges against the move should not have to open the order to read
          what the customer actually sent. Drawn on the pro bar because it describes the
          PRO, not this invoice: an accessorial block carries it too, and opens the same
          records as its base. */}
      {row.hasEdi && (
        <>
          <span className="opacity-50">|</span>
          <button
            type="button"
            onClick={() => onOpenEdi(row.pronumber)}
            className="underline-offset-2 hover:underline"
            title="View EDI Info"
          >
            EDI Info
          </button>
        </>
      )}
      <span className="ml-auto flex flex-wrap items-center gap-2">
        {excluded && (
          <span className="rounded bg-white/20 px-1.5 py-0.5 font-bold">
            NOT ON THIS INVOICE
          </span>
        )}
        {row.invoiceDeleted && (
          <span className="rounded bg-brand-red px-1.5 py-0.5 font-bold text-white">
            INVOICE DELETED
          </span>
        )}
        {row.orderDeleted && (
          <span className="rounded bg-brand-red px-1.5 py-0.5 font-bold text-white">
            ORDER DELETED
          </span>
        )}
        <span
          className={credithold === 'Y'
            ? 'rounded bg-brand-red px-1.5 py-0.5 font-bold text-white'
            : ''}
        >
          {credithold === 'Y' && 'CREDIT HOLD · '}
          {billtoname} ({billto})
        </span>
      </span>
    </Bar>

    <div className="grid grid-cols-1 gap-px bg-hairline lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)_minmax(0,1.35fr)]">
      {/* --- column 1: the order, V1's left field list --- */}
      <div className="bg-panel py-1">
        <Row label="CSR" value={dash(row.csrname)} />
        <Row label="WCSR" value={dash(row.whsecsrname)} />
        <Row label="Equip. No." value={dash(row.container)} highlight />
        <Row label="Ch. No." value={dash(row.chassisid)} />
        <Row label="Size / Type" value={pair(row.size, row.equiptype)} />
        <Row label="BOL/BKG/Ref" value={dash(row.bol)} highlight />
        <Row label="Bill To Ref" value={dash(row.billtorefno)} highlight />
        <Row label="PO No." value={dash(row.po)} />
        <Row label="SS Line" value={dash(row.ssline)} />
        <Row label="Vsl/Voy" value={pair(row.vessel, row.voyage)} highlight />
        <Row label="Lbs. / Kgs." value={pair(row.lbs, row.kgs)} />
        <Row label="Seal No." value={dash(row.sealnumber)} />
        <Row label="Commodity" value={dash(row.commodity)} />
        <Row label="Rel. No." value={dash(row.origapptnumber)} />
        <Row label="Port Orig." value={dash(row.portoforiginname)} />
        <Row label="Port Disch." value={dash(row.portofdischargename)} />
        <Row label="IDC" value={dash(row.dcnumber)} />
        <Row label="Mode" value={dash(row.mode)} />
        <Row label="Dept" value={dash(row.dept)} />
        <Row label="Priority" value={dash(row.priority)} />
        <Row label="Deleted" value={row.orderDeleted ? 'Y' : 'N'} />
        <Row label="Created By" value={dash(row.createdby)} />
        <Row label="Create Date" value={dash(dateTime(row.createdatetime))} />
        <Row label="Last Change" value={dash(row.lastchangedby)} />
        <Row label="Change Date" value={dash(dateTime(row.lastchangedatetime))} />
        <Row label="Rate Zone" value={dash(row.templatetype)} />
      </div>

      {/* --- column 2: stamps, flags, the two dates the charges rest on --- */}
      <div className="space-y-2 bg-panel px-2 py-2">
        <div className="flex flex-wrap gap-1">
          <Stamp label="I:" value={row.invoicedate} />
          <Stamp label="M:" value={row.masexportdate} />
        </div>

        <div className="grid grid-cols-2 gap-x-2 gap-y-[2px]">
          {row.flags.map((f) => <Flag key={f.label} flag={f} />)}
        </div>

        {/* Always highlighted — the charges are computed from these two. */}
        <div className="space-y-[2px]">
          <Row label="Deliver Date" value={dash(dateOnly(row.deliveredAt))} highlight />
          <Row label="Per Diem Date" value={dash(row.perdiemstartdate)} highlight />
        </div>

        {/* The same two the Addl Cost tab shows, under the two dates the charges rest on,
            because they are the same kind of thing: an input the CHASSIS MANAGEMENT line
            is computed from. The window is kept alongside the count rather than dropped —
            it is what makes the number checkable without opening the pro, which is the
            trip this is meant to save. */}
        <div className="space-y-[2px]">
          <Row label="Chassis Days" value={dash(row.chassisDays)} />
          <Row label="Chassis Usage" value={dash(row.chassisUsage)} />
          {/* The Addl Cost tab's rule, expression for expression (AddlCostView.tsx:589,
              V1 chassisfeeamount>0 && !haschassisfee): a fee was calculated and no live
              invoice on the pro bills it. Red rather than yellow because this is the same
              statement as COD and credit hold — money that will not arrive the usual way
              unless somebody acts. */}
          <Row
            label="Calc Chassis Fee"
            value={row.chassisFeeAmount ? money(row.chassisFeeAmount) : undefined}
            alert={(row.chassisFeeAmount ?? 0) > 0 && !row.hasChassisFee}
          />
        </div>

        <DriverPayAdditions items={row.driverPay} />

        {row.invoicecomments && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted">Invoice comments</p>
            <p className="whitespace-pre-wrap rounded bg-sunken px-2 py-1 text-[12px] text-body">
              {row.invoicecomments}
            </p>
          </div>
        )}
        {row.comments && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted">Order comments</p>
            <p className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-sunken px-2 py-1 text-[12px] text-body">
              {row.comments}
            </p>
          </div>
        )}
      </div>

      {/* --- column 3: the move, then the charges --- */}
      <div className="bg-panel">
        <div className="overflow-x-auto border-b border-hairline">
          <LegStrip legs={row.legs} />
        </div>

        {/* Why LABELS here and the specifics in the banner above: a hold's `detail` is
            the union across every pro it names — one pro's missing paperwork would be
            printed under another's. `requiredDocs` is the exception, being genuinely
            per-row, so it is shown. */}
        {holds.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-hairline px-3 py-1.5">
            {holds.map((h) => (
              <span
                key={h.code}
                className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                  h.v1Silent ? HL : HL_RED
                }`}
                title={h.v1Silent
                  ? `${h.label} — V1 computes this but never blocks on it`
                  : h.label}
              >
                {h.label}
              </span>
            ))}
            {row.requiredDocs.length > 0 && (
              <span className="text-[12px] text-danger-fg">
                Awaiting: {row.requiredDocs.join(', ')}
              </span>
            )}
          </div>
        )}

        <QuoteRef
          quoteNumber={row.quoteNumber}
          revisionNumber={row.revisionNumber}
          fromMain={row.quoteFromMain}
          onOpen={onOpenQuote}
        />

        {/* WHAT KIND OF RECORD THIS IS, said where it matters.
            It used to be a grey chip in the pro bar above, among the sage tag and the
            bill-to — technically present, and billers reported missing it, which on a
            screen where an accessorial is billed differently from a dray is the one
            thing you cannot afford to miss. V1 paints a full-width orange bar directly
            over the charge lines and nobody misses it there, so this does the same:
            next to the charges it qualifies, at a size that reads before the numbers do.
            The remove action comes with it rather than staying behind in the bar —
            `removable` is only ever set on an accessorial, and the label and its action
            belong together as they do in V1. */}
        {row.accessorialnum !== 0 && (
          <div
            className={`flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-3 py-1.5 ${HL_ACCESSORIAL}`}
          >
            <span className="text-sm font-bold uppercase tracking-wide">
              Accessorial {row.accessorialnum}
            </span>
            {removable && (
              <button
                type="button"
                onClick={onToggleExcluded}
                className="text-[12px] font-semibold underline underline-offset-2 hover:opacity-80"
                title={excluded
                  ? 'Put this accessorial back on the invoice'
                  : 'Leave this accessorial off the invoice — it stays uninvoiced'}
              >
                {excluded ? 'Add back' : 'Remove from this invoice'}
              </button>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-[12px]">
            <thead>
              <tr className="bg-slate-700 text-[10px] uppercase tracking-wider text-white dark:bg-gray-700">
                <th className="w-10 px-2 py-1 text-right font-semibold">Line</th>
                <th className="px-2 py-1 text-left font-semibold">Item</th>
                <th className="px-2 py-1 text-left font-semibold">MasCode</th>
                <th className="px-2 py-1 text-right font-semibold">Qty</th>
                <th className="px-2 py-1 text-right font-semibold">Rate</th>
                <th className="px-2 py-1 text-left font-semibold">Type</th>
                <th className="px-2 py-1 text-left font-semibold">Description</th>
                <th className="px-2 py-1 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {row.lineItems.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-3 text-center text-muted">
                    No charges on this invoice.
                  </td>
                </tr>
              )}
              {row.lineItems.map((li) => {
                const noAmount = !!li.liitem && li.litotal === 0;
                return (
                  <tr key={li.linumber} className="border-b border-hairline">
                    {/* V1 prints the SLOT NUMBER PLUS ONE (lib_invoice.pl:962,
                        `($j+1)`), so its first line reads 1 where the column holds 0.
                        Gaps are faithful: a blanked line keeps its slot, and V1 skips it
                        while still numbering from the slot. */}
                    <td className="px-2 py-1 text-right tabular-nums text-muted">{li.linumber + 1}</td>
                    <td className="px-2 py-1 font-medium text-body">{dash(li.liitem)}</td>
                    <td className={`px-2 py-1 ${li.limastype ? 'text-body' : 'font-medium text-danger-fg'}`}>
                      {li.limastype || 'no GL code'}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-body">{li.liqty}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-body">{money(li.lirate)}</td>
                    <td className="px-2 py-1 text-body">{dash(li.liratetype)}</td>
                    <td className="px-2 py-1 text-body">{li.lidescription}</td>
                    <td
                      className={`px-2 py-1 text-right tabular-nums ${
                        noAmount ? `${HL_RED} font-bold` : 'text-body'
                      }`}
                    >
                      {money(li.litotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {row.creditamt !== 0 && (
                <tr>
                  <td colSpan={7} className="px-2 py-1 text-right text-muted">Credit Amount</td>
                  <td className="px-2 py-1 text-right tabular-nums text-body">
                    -{money(row.creditamt)}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={7} className="px-2 py-1 text-right font-semibold text-body">Total</td>
                <td className={`px-2 py-1 text-right font-bold tabular-nums ${HL}`}>
                  {money(row.netTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <DriverPayBar
          margin={margin}
          pending={marginPending}
          onOpen={() => onOpenMargin(row.pronumber)}
        />

        <AccessorialsStrip
          accessorials={row.accessorials}
          onOpen={onOpenAccessorial}
          pageRecnos={pageRecnos}
        />
      </div>
    </div>
  </div>
);

/* ----------------------------------------------------------------- page */

export const InvoiceListContent: React.FC<{
  params: InvoiceListParams;
  onSelect: (params: InvoiceListParams) => void;
}> = ({ params, onSelect }) => {
  const requestVersion = useRef(0);
  const [data, setData] = useState<InvoiceListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerPro, setDrawerPro] = useState<number | null>(null);
  // The two rate-engine holds arrive after the page has painted — see below.
  const [marginHolds, setMarginHolds] = useState<InvoiceHold[] | null>(null);
  const [marginSkipped, setMarginSkipped] = useState<string[]>([]);
  const [marginSkips, setMarginSkips] = useState<InvoiceSkip[]>([]);
  const [proMargins, setProMargins] = useState<InvoiceListProMargin[]>([]);
  const [marginPro, setMarginPro] = useState<number | null>(null);
  /** The accessorial the Accessorials strip opened, if any. */
  const [accEdit, setAccEdit] = useState<{ pro: number; recno: number } | null>(null);
  const [quoteRef, setQuoteRef] = useState<{ quoteNumber: number; revisionNumber: number } | null>(null);
  const [ediPro, setEdiPro] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [options, setOptions] = useState<InvoiceOptionsMode | null>(null);
  // Feedback from an action that did not reload the page — an EDI flag, a sent e-mail,
  // a rendered estimate. Kept off `error`, which means "the invoice would not load":
  // a failed resend has not cost you the screen.
  /** Accessorial charges the user has taken off this invoice before raising it. V1 keeps
   *  the same list in a session cookie and filters its WHERE with it (lib_invoice.pl:224);
   *  page state is the same answer without a cookie that outlives the screen — V1 never
   *  clears its own, so a removal there can follow you to the next invoice. */
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  /** The holds re-asked for what is still ON the invoice, once anything has been removed.
   *  `null` means "nothing removed, the load's own verdict stands". */
  const [selectionHolds, setSelectionHolds] = useState<
    { holds: InvoiceHold[]; skipped: string[]; skips: InvoiceSkip[] } | null
  >(null);
  /** True while the re-ask is in flight. The verdict on screen is the OLD one until it
   *  lands, and acting on a stale verdict is exactly the bug this fixes — so Create is
   *  held shut for the moment it takes. */
  const [revalidating, setRevalidating] = useState(false);
  /** A run's outcome, not just a sentence. A separated run half-succeeds by design, and
   *  the invoices it DID raise leave this screen the moment they are raised — so without
   *  naming them here there is nowhere left to see what just happened. */
  const [pageNote, setPageNote] = useState<{
    tone: 'ok' | 'bad';
    text: string;
    created?: { recno: number; invoiceNumber: string }[];
    held?: HeldInvoice[];
  } | null>(null);
  /** Set when the note describes something that is about to move the page — creating an
   *  invoice navigates to it, and the load that follows must not wipe the confirmation
   *  it caused. Every other load clears the note, so it cannot follow you to an
   *  unrelated invoice. */
  const keepNoteOnce = useRef(false);
  const ediBusy = useRef(false);
  // V1 offers an Invoice Type next to Create Invoice, defaulting to 'A' when the invoice
  // carries accessorials (lib_invoice.pl:858). It is our invoiceClass.
  const [invoiceClass, setInvoiceClass] = useState<InvoiceClass>('');
  // ONE request id per creation attempt, reused across retries. `invoice_creation_requests`
  // only makes a lost response safe to retry if the id is stable — a fresh UUID on the
  // second click is a second invoice. Cleared when the selection changes.
  const createRequestId = useRef<string | null>(null);
  // Set by the create call, read once by onDone. Sending outlives the request, so the
  // create response hands back a handle rather than a result and the page reports that
  // the messages are on their way — not that they have arrived.
  const emailBatch = useRef<{ batch: number; invoices: number } | null>(null);
  // Charges the run refused. Evaluated per charge now, so a run can half-succeed — and a
  // skipped invoice nobody is told about is worse than a refused run, because the charge
  // just quietly stays unbilled.
  const heldItems = useRef<HeldInvoice[]>([]);
  const createdInvoices = useRef<{ recno: number; invoiceNumber: string }[]>([]);
  // How far the batched margin pass has got. `null` = not started/not applicable.
  const [marginProgress, setMarginProgress] = useState<{ done: number; total: number } | null>(null);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setData(null);
    setError('');
    if (keepNoteOnce.current) keepNoteOnce.current = false;
    else setPageNote(null);
    setExcluded(new Set());
    setSelectionHolds(null);
    setRevalidating(false);
    setMarginHolds(null);
    setMarginSkipped([]);
    setMarginSkips([]);
    setProMargins([]);
    setMarginProgress(null);
    try {
      const response = await getInvoiceList(params);
      if (requestVersion.current !== version) return;
      setData(response);
    } catch (e: unknown) {
      if (requestVersion.current !== version) return;
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : e instanceof Error ? e.message : 'Could not load the invoice.');
      setData(null);
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
    return () => { requestVersion.current += 1; };
  }, [load]);

  /** V1's resend / cancel-resend / send controls (lib_invoice.pl:1226-1250).
   *
   * The response carries the new state, so `data.edi` is patched in place instead of
   * reloading: a reload would throw away a margin pass that costs a second per pro and
   * repaint the whole invoice to change three characters.
   *
   * Guarded with a ref, not state — two clicks in the same tick both read the pre-render
   * value, and these are not idempotent in the way that matters (resend then
   * cancel-resend would leave the flag off).
   */
  const onEdiSend = async (action: EdiSendAction) => {
    if (ediBusy.current || !data?.target?.invhistoryrecno) return;
    ediBusy.current = true;
    setPageNote(null);
    try {
      const result = await requestEdiSend(data.target.invhistoryrecno, action);
      setData((prev) => (prev ? { ...prev, edi: { ...result } } : prev));
      setPageNote({ tone: 'ok', text: `${result.applied} — the EDI script will pick this up on its next run.` });
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message;
      setPageNote({ tone: 'bad', text: message || 'Could not change the EDI status.' });
      // A 409 means somebody else moved the flag. Reload so the control offers what the
      // invoice actually allows now rather than what it allowed when the page opened —
      // and keep the message, which is the reason the page just moved under them.
      keepNoteOnce.current = true;
      void load();
    } finally {
      ediBusy.current = false;
    }
  };

  // V1 preselects 'A' when the invoice has accessorial charges, and Standard otherwise.
  const hasAccessorial = data?.status === 'resolved' && data.rows.some((r) => r.accessorialnum > 0);
  useEffect(() => { setInvoiceClass(hasAccessorial ? 'A' : ''); }, [hasAccessorial]);

  // MISSING DRIVER RATES and MARGIN APPROVAL REQ'D need a driver-pay rate pass per pro,
  // about a second each — thirteen seconds on a nine-pro masterpro. Fetching them after
  // the page has painted is the whole reason the invoice appears immediately. A failure
  // here leaves the "still checking" line in place rather than claiming a clean result:
  // silently dropping a hold is the one outcome this screen must not produce.
  const marginPending = data?.status === 'resolved' && data.marginPending;
  const marginPros = data?.status === 'resolved' ? data.pronumbers : undefined;
  useEffect(() => {
    if (!marginPending || !marginPros?.length) return;
    let cancelled = false;
    (async () => {
      // ONE CALL for anything up to the cap, which is every realistic invoice — the
      // margin pass is batched server-side now and costs nine queries whether it is asked
      // for 150 pros or 500. The old [10, 25] ramp existed because that pass used to cost
      // a second PER PRO; splitting it now just re-pays a whole batch per request. See
      // MARGIN_BATCH_SCHEDULE for the measurements.
      //
      // The loop stays: masterpros do get bigger than the cap, and when they do the
      // batches remain SEQUENTIAL — firing them together would move the queue from the
      // browser to the database without anything arriving sooner. Hold merging and the
      // progress counter below are what make a split invisible to the user.
      const all: InvoiceListProMargin[] = [];
      const holds = new Map<string, InvoiceHold>();
      const skipped = new Set<string>();
      const skips = new Map<string, InvoiceSkip>();
      setMarginProgress({ done: 0, total: marginPros.length });
      let done = 0;
      for (const batch of marginBatches(marginPros)) {
        if (cancelled) return;
        try {
          const res = await getMarginHolds(params, batch);
          if (cancelled) return;
          all.push(...res.margins);
          // Holds merge by code across batches: a hold raised on batch 1 and again on
          // batch 4 is one banner naming both sets of pros, not two banners.
          res.holds.forEach((h) => {
            const prev = holds.get(h.code);
            holds.set(h.code, prev
              ? { ...h,
                  detail: [...prev.detail, ...h.detail],
                  pronumbers: [...prev.pronumbers, ...h.pronumbers],
                  invoicerecnos: [...(prev.invoicerecnos ?? []), ...(h.invoicerecnos ?? [])] }
              : h);
          });
          res.skipped.forEach((x) => skipped.add(x));
          // Merged by reason, the way the holds merge by code: one skip naming every pro
          // it is true of, not one per batch.
          (res.skips ?? []).forEach((s) => {
            const prev = skips.get(s.reason);
            skips.set(s.reason, prev
              ? { ...s,
                  pronumbers: [...prev.pronumbers, ...s.pronumbers],
                  invoicerecnos: [...(prev.invoicerecnos ?? []), ...(s.invoicerecnos ?? [])] }
              : s);
          });
        } catch {
          if (cancelled) return;
          const reason = `Driver rates and margin could not be checked for pro ${batch.join(', ')}.`;
          skipped.add(reason);
          // Attributed to the batch that failed, not left bare. An unattributed skip
          // refuses EVERY charge, so one failed batch would block a whole separated run
          // over pros it never even looked at.
          skips.set(reason, { reason, pronumbers: [...batch], invoicerecnos: [] });
        }
        done += batch.length;
        setProMargins([...all]);
        setMarginHolds([...holds.values()]);
        setMarginSkipped([...skipped]);
        setMarginSkips([...skips.values()]);
        setMarginProgress({ done, total: marginPros.length });
      }
    })();
    return () => { cancelled = true; };
  }, [marginPending, marginPros, params]);

  /** True only once every batch is in. Until then the ABSENCE of a margin hold means
   *  nothing, and the page must not let it read as a clean bill. */
  const marginComplete = !marginPending
    || (!!marginProgress && marginProgress.done >= marginProgress.total);

  /** Re-ask the server once anything is taken off the invoice.
   *
   *  V1 does this for free: "Remove from this invoice" round-trips through
   *  `removefrominvoice` and the next render appends `AND invg.recno NOT IN (...)` to the
   *  WHERE (lib_invoice.pl:224-233), so the removed charge is simply not there to be
   *  judged. Here the row stays on screen, badged — which left the EDI banner naming a
   *  line item that was no longer being billed AND still refusing the charges it was
   *  attributed to, so removing the offending accessorial did not unblock the invoice.
   *
   *  Deliberately NOT a full `load()`: that would clear `excluded` (undoing the removal),
   *  drop `data.edi`, and throw away a margin pass that costs a second per pro. */
  useEffect(() => {
    const dropped = [...excluded];
    if (!dropped.length) { setSelectionHolds(null); setRevalidating(false); return; }
    let cancelled = false;
    setRevalidating(true);
    void (async () => {
      try {
        const res = await getSelectionHolds(params, dropped);
        if (cancelled) return;
        setSelectionHolds({ holds: res.holds, skipped: res.skipped, skips: res.skips });
      } catch {
        // Keep the load's verdict rather than inventing a clean one — a failed re-ask
        // must never be the reason an invoice goes out.
        if (!cancelled) setSelectionHolds(null);
      } finally {
        if (!cancelled) setRevalidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [excluded, params]);

  /** The server's verdict for what is still on the invoice, falling back to the load's
   *  own when nothing has been removed. The margin holds are per-PRO and unaffected by a
   *  removal, so they are merged in either way. */
  const allHolds = useMemo(
    () => [...(selectionHolds?.holds ?? data?.holds ?? []), ...(marginHolds ?? [])],
    [data, selectionHolds, marginHolds],
  );
  const allSkipped = useMemo(
    () => [...(selectionHolds?.skipped ?? data?.skipped ?? []), ...marginSkipped],
    [data, selectionHolds, marginSkipped],
  );
  const allSkips = useMemo(
    () => {
      const attributed = [...(selectionHolds?.skips ?? data?.skips ?? []), ...marginSkips];
      // A reason with no attributed twin is a skip we cannot place, and an unplaceable
      // skip refuses everything. `skipped` is derived from `skips` server-side so the two
      // cannot diverge there — this covers a response that predates the attribution, where
      // reading only `skips` would silently let a held selection through.
      const placed = new Set(attributed.map((s) => s.reason));
      const unplaceable = allSkipped
        .filter((reason) => !placed.has(reason))
        .map((reason) => ({ reason, pronumbers: [], invoicerecnos: [] }));
      return [...attributed, ...unplaceable];
    },
    [data, selectionHolds, marginSkips, allSkipped],
  );

  const marginByPro = useMemo(
    () => new Map(proMargins.map((m) => [m.pronumber, m])),
    [proMargins],
  );

  const target = data?.target ?? null;

  /** V1's condition (lib_invoice.pl:926): uninvoiced, an ACCESSORIAL, and more than one
   *  row on the page — with nothing else to bill there is no invoice left to remove it
   *  from. */
  const canRemoveRows = !!target?.isNew && !target.invoiceDeleted && (data?.rows.length ?? 0) > 1;
  /** Charges that could be billed at all.
   *
   *  TWO DIFFERENT `invoiceDeleted`s meet on this page and the gates below were reading
   *  the wrong one:
   *    * `row.invoiceDeleted`    — THIS charge's `invoice.deleted` (rows.py). What the
   *                                red INVOICE DELETED badge shows.
   *    * `target.invoiceDeleted` — the `invoice_history` row was CANCELLED (target.py).
   *  An un-invoiced pro whose accessorial was deleted has the first set and the second
   *  clear, so every `!target.invoiceDeleted` gate passed and the page offered to raise
   *  an invoice from a charge the server refuses (creation.py assert_selection). */
  const billableRows = useMemo(
    () => (data?.rows ?? []).filter((r) => !r.invoiceDeleted),
    [data],
  );
  /** Every row with a block on the page. A removed (excluded) accessorial still has its
   *  block — dimmed, with Add back — so it still counts as being down there. */
  const pageRecnos = useMemo(
    () => new Set((data?.rows ?? []).map((r) => r.invoicerecno)),
    [data],
  );
  /** True once anything on the page cannot be billed — drives the headline total away
   *  from the server's `data.total`, which counts every row it returned. */
  // `data?.rows?.length`, not `data?.rows.length`: an ambiguous (picker) response is a
  // truthy `data` with no `rows` at all, and this is evaluated unconditionally —
  // unlike `canRemoveRows` below, which short-circuits on `target` first.
  const hasDeletedRows = (data?.rows?.length ?? 0) !== billableRows.length;
  /** What Create Invoice and View Estimate actually act on. Deleted rows stay VISIBLE in
   *  the grid (badge and all) — they are just not billable. */
  const includedRows = useMemo(
    () => billableRows.filter((r) => !excluded.has(r.invoicerecno)),
    [billableRows, excluded],
  );
  const includedTotal = useMemo(
    () => includedRows.reduce((sum, r) => sum + r.netTotal, 0),
    [includedRows],
  );
  const toggleExcluded = (invoicerecno: number) => setExcluded((current) => {
    const next = new Set(current);
    if (!next.delete(invoicerecno)) next.add(invoicerecno);
    return next;
  });

  /** The charges this selection could still raise an invoice for.
   *
   *  Holds are evaluated PER CHARGE now, so one pro out of fifteen being on hold must not
   *  hide the button for the other fourteen — that was the whole point of the per-item
   *  work, and gating the screen on the aggregate quietly undid it. `holdApplies` is the
   *  mirror of the server's `Hold.applies_to`; the server still decides, and refuses
   *  anything this lets through. */
  const creatableRows = useMemo(() => {
    const blocking = allHolds.filter((h) => !h.v1Silent);
    return includedRows.filter((r) => !(
      blocking.some((h) => holdApplies(h, r.invoicerecno, r.pronumber))
      || allSkips.some((s) => holdApplies(s, r.invoicerecno, r.pronumber))
    ));
  }, [allHolds, allSkips, includedRows]);

  /** V1 renders Create Invoice only in the ELSE arm of `if ($invoicinghold)`
   *  (lib_invoice.pl:826/836): a held invoice gets banners, not a button. That is still
   *  right — offering a control the server will refuse is worse than not offering it,
   *  and the banners above already say why.
   *
   *  WHAT CHANGED IS WHAT "HELD" MEANS. It used to be any hold anywhere in the selection,
   *  which on a separating bill-to hid the button because one pro out of fifteen was
   *  waiting on an approval — and the other fourteen invoices, which the server would
   *  happily have raised, could not be reached from this screen at all.
   *
   *  So: a SEPARATING bill-to raises one invoice per charge, and is blocked only when
   *  NOTHING is clear. A GROUPED bill-to raises one invoice from every charge, so a single
   *  held charge refuses it — exactly the old behaviour, which is why this reads the mode
   *  rather than assuming one.
   *
   *  The margin pass being merely INCOMPLETE still leaves the button visible but disabled;
   *  hiding it for the forty seconds a ninety-pro invoice takes would read as "never". */
  const separately = data?.invoiceSeparately === 'ALWAYS'
    || (data?.invoiceSeparately === 'MAIN' && invoiceClass === '');
  const createBlocked = separately
    ? creatableRows.length === 0
    : creatableRows.length < includedRows.length;

  const heading = target
    ? target.invoicenumber || `Masterpro ${target.masterpro}`
    : 'Invoice';

  return (
    <div className="min-h-full bg-app p-3">
      <div className="mx-auto max-w-[1800px] space-y-3">
        {/* --- header ---
            STICKY. A 90-pro invoice scrolls a long way, and the total and the bill-to
            are what every row is being read against — losing them off the top is losing
            the question you are asking. z-20 and an opaque panel background so the pro
            blocks' own dark bars scroll behind it rather than through it. */}
        <div className="sticky top-0 z-20 overflow-hidden rounded-lg border border-rule bg-panel shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-heading">{heading}</h1>
              {target && (
                <p className="flex flex-wrap items-baseline gap-2 text-xs text-muted">
                  {target.isNew
                    ? 'Not invoiced yet — these orders are what an invoice would be raised from.'
                    : `Invoice ${target.invoicenumber}`}
                  {target.invoiceDeleted && (
                    <span className="rounded bg-brand-red px-1.5 py-0.5 font-bold text-white">
                      CANCELLED
                    </span>
                  )}
                </p>
              )}
            </div>
            {target && (
              <div className="flex items-center gap-4">
                {/* V1 puts Cancel Invoice on the invoice itself (lib_invoice.pl:866), gated
                    by the `cancelinvoice` ACL, only once an invoice exists, and only while
                    NOTHING on it has reached Sage (:859) — cancelling an exported invoice
                    would leave CMS and Sage disagreeing about money. The server refuses it
                    too; this only keeps the button from lying about what it will do. */}
                {!target.isNew && !target.invoiceDeleted && target.invhistoryrecno > 0
                  && !(data?.rows ?? []).some((r) => r.masexportdate) && (
                  <button
                    type="button"
                    onClick={() => setCancelling(true)}
                    className="text-sm text-danger-fg hover:underline"
                    title="Release these charges back to uninvoiced"
                  >
                    Cancel Invoice
                  </button>
                )}
                {/* `billableRows`, not `data.rows`: with every charge deleted there is
                    nothing to raise AND nothing to estimate, so neither control belongs.
                    Manually removing every row is deliberately NOT the same case — that
                    leaves the block up with Create disabled and a tooltip saying why. */}
                {target.isNew && !target.invoiceDeleted && billableRows.length > 0 && (
                  <div className="flex items-center gap-2">
                    {/* V1 shows View Estimate whenever the charges are uninvoiced, hold
                        or no hold (lib_invoice.pl:726 — the condition on $invoicinghold
                        was deliberately removed). Quoting the customer a number while
                        the paperwork is being sorted out is the use case, so this is NOT
                        gated on whether the invoice can be created. */}
                    <button
                      type="button"
                      onClick={() => setOptions({ kind: 'estimate', invoiceRecnos: includedRows.map((r) => r.invoicerecno) })}
                      className="text-sm text-accent-text hover:underline"
                      title="The invoice these charges would produce — nothing is created"
                    >
                      View Estimate
                    </button>
                    {/* Create Invoice and the Type it would be raised as: V1 renders
                        NEITHER while the invoice is held — the hold branch and the create
                        branch are the two arms of one if (lib_invoice.pl:826/836), so a
                        held invoice has no button and no dropdown at all. Offering a
                        control the server will refuse is worse than not offering it: the
                        banners above already say why. */}
                    {!createBlocked && (
                      <>
                        <label className="text-xs text-muted">
                          Type{' '}
                          <select
                            value={invoiceClass}
                            onChange={(e) => setInvoiceClass(e.target.value as InvoiceClass)}
                            aria-label="Invoice Type"
                            className="rounded border border-rule bg-input px-1 py-0.5 text-xs text-body"
                          >
                            {INVOICE_CLASS_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={() => setOptions({
                            kind: 'create',
                            invoiceRecnos: includedRows.map((r) => r.invoicerecno),
                            // The counts the gate above already worked out. The server
                            // re-evaluates and is still the authority; this is so the
                            // confirmation can say what will happen.
                            willCreate: creatableRows.length,
                            willHold: includedRows.length - creatableRows.length,
                          })}
                          disabled={!marginComplete || revalidating || includedRows.length === 0}
                          title={includedRows.length === 0
                            ? 'Every charge has been removed from this invoice'
                            : revalidating ? 'Re-checking what is left on this invoice'
                            : marginComplete ? undefined : 'Still checking driver rates and margin'}
                          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Create Invoice
                        </button>
                      </>
                    )}
                  </div>
                )}
                {/* V1's EDI controls (lib_invoice.pl:1226-1250), gated there by the
                    `edi_resend-cancel` ACL and shown only on an invoice EDI actually
                    handled. Exactly one is ever offered, and which one is the server's
                    answer — `donotsend` beats `resend` beats `sent`, V1's branch order.
                    Nothing sends from here: each flips a flag the EDI script reads on
                    its next run, which is why the confirmation says so. */}
                {!target.isNew && !target.invoiceDeleted && data?.edi && data.edi.action !== '' && (
                  <button
                    type="button"
                    onClick={() => void onEdiSend(data.edi!.action as EdiSendAction)}
                    className="text-sm text-accent-text hover:underline"
                    title={EDI_ACTION_TITLES[data.edi.action]}
                  >
                    {EDI_ACTION_LABELS[data.edi.action]}
                  </button>
                )}
                {/* Once it exists, the same modal views, prints and e-mails it. */}
                {!target.isNew && !target.invoiceDeleted && target.invhistoryrecno > 0 && (
                  <button
                    type="button"
                    onClick={() => setOptions({ kind: 'existing', invoiceHistoryRecno: target.invhistoryrecno })}
                    className="text-sm text-accent-text hover:underline"
                    title="View, print or e-mail this invoice"
                  >
                    Invoice Options
                  </button>
                )}
                {data && data.pronumbers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => openProSearch(data.pronumbers)}
                    className="text-sm text-accent-text hover:underline"
                    title="Open every pro on this invoice in one search"
                  >
                    Open all {data.pronumbers.length} pros
                  </button>
                )}
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wide text-muted">Total</div>
                  <div className={`rounded px-2 py-0.5 text-xl font-bold tabular-nums ${HL}`}>
                    {/* `data.total` is every row the server returned, which on a NOT-YET
                        -invoiced page includes ones that cannot be billed. Fall back to
                        the included sum whenever the two can disagree. On an existing
                        invoice `data.total` stays authoritative — it is what was billed. */}
                    {money(target?.isNew && (excluded.size || hasDeletedRows)
                      ? includedTotal
                      : (data?.total ?? 0))}
                  </div>
                  {excluded.size > 0 && (
                    <div className="text-[11px] text-muted">
                      {excluded.size} removed · was {money(data?.total ?? 0)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {target && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-hairline bg-sunken px-4 py-2 sm:grid-cols-4 lg:grid-cols-6">
              {([
                [target.credithold === 'Y' ? 'Bill To — CREDIT HOLD' : 'Bill To', target.billtoname],
                ['Bill To #', `${target.billto}`],
                ['Sage Tag', target.mas90code],
                ['Terms', target.creditterms],
                ['Masterpro', target.masterpro],
                ['Address', [...target.billtoAddress, target.billtoCityState,
                  target.billtoZipCountry].filter(Boolean).join(', ')],
              ] as Array<[string, string]>).map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
                  <div
                    className={`truncate text-[13px] ${
                      label.includes('CREDIT HOLD') ? 'font-bold text-danger-fg' : 'text-body'
                    }`}
                    title={value || undefined}
                  >
                    {value || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {pageNote && (
            <div
              role="status"
              className={`border-t border-hairline px-4 py-2 text-[13px] ${
                pageNote.tone === 'ok'
                  ? 'bg-ok text-ok-fg'
                  : 'bg-danger text-danger-fg'
              }`}
            >
              <div>{pageNote.text}</div>

              {/* The invoices the run raised. They are gone from this screen the instant
                  they exist — the search resolves to what is still UNINVOICED — so this is
                  the only place their numbers appear. Clickable, because the next thing
                  anyone does is open one. */}
              {pageNote.created && (
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-[11px] uppercase tracking-wide opacity-80">Created</span>
                  {pageNote.created.map((c) => (
                    <button
                      key={c.recno}
                      type="button"
                      onClick={() => onSelect({ invhistoryrecno: c.recno })}
                      className="underline underline-offset-2 tabular-nums hover:no-underline"
                      title={`Open invoice ${c.invoiceNumber}`}
                    >
                      {c.invoiceNumber}
                    </button>
                  ))}
                </div>
              )}

              {/* Named, not counted. "2 sets of charges were held" sends someone hunting;
                  the PRO and the reason is what they need to fix it and run the rest
                  again. */}
              {pageNote.held && (
                <ul className="mt-1 space-y-0.5">
                  {pageNote.held.map((h) => (
                    <li key={h.invoiceRecnos.join(',')}>
                      <span className="font-semibold tabular-nums">{h.pronumbers.join(', ')}</span>
                      {' — '}
                      {h.reasons.join('; ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {loading && <p className="text-sm text-muted">Loading…</p>}

        {!loading && error && (
          <div className="rounded border-l-4 border-danger-fg bg-danger px-3 py-2 text-sm text-danger-fg">
            {error}
          </div>
        )}

        {!loading && !error && (data?.status === 'storage' || data?.status === 'empty') && (
          <div className="rounded-lg border border-rule bg-panel px-4 py-6 text-sm text-body">
            {data.reason || 'Nothing matched.'}
          </div>
        )}

        {/* --- the picker --- */}
        {!loading && !error && data?.status === 'ambiguous' && (
          <div className="overflow-hidden rounded-lg border border-rule bg-panel">
            <Bar>
              {data.candidates.length} invoices match — pick one
              <span className="font-normal normal-case opacity-80">
                they differ by {PICKFIELD_LABELS[data.pickfield] ?? data.pickfield}
              </span>
            </Bar>
            <ul className="divide-y divide-hairline">
              {data.candidates.map((c) => (
                <li key={`${c.masterpro}-${c.billto}-${c.invhistoryrecno}`}>
                  <button
                    type="button"
                    onClick={() =>
                      onSelect(
                        c.invhistoryrecno
                          ? { invhistoryrecno: c.invhistoryrecno }
                          : { masterpro: c.masterpro, billto: c.billto },
                      )
                    }
                    className="flex w-full items-baseline gap-4 px-4 py-2 text-left text-sm hover:bg-highlight"
                  >
                    <span className="w-28 shrink-0 font-medium tabular-nums text-accent-text">
                      {c.masterpro}{c.invoicetype}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-body">
                      {c.billtoname} <span className="text-muted">({c.billto})</span>
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted">
                      {c.invoicedate ? dateOnly(c.invoicedate) : 'not invoiced'}
                      {c.deleted && ' · cancelled'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* --- holds --- */}
        {!loading && data?.status === 'resolved'
          && (allHolds.length > 0 || (marginPending && !marginComplete)) && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline gap-2">
              {allHolds.some((h) => !h.v1Silent) && (
                <span className="rounded bg-brand-red px-2 py-0.5 text-xs font-bold text-white">
                  INVOICING HOLD
                </span>
              )}
              {!data.holdsEvaluated && (
                <span className="text-xs text-muted">
                  The invoice already exists, so only the EDI check runs.
                </span>
              )}
              {marginPending && !marginComplete && (
                <span className="text-xs text-muted">
                  Still checking driver rates and margin
                  {marginProgress && marginProgress.done < marginProgress.total
                    && ` — ${marginProgress.done} of ${marginProgress.total} pros`}…
                </span>
              )}
            </div>
            {allHolds.map((h) => <HoldBanner key={h.code} hold={h} />)}
          </div>
        )}

        {!loading && data?.status === 'resolved' && allSkipped.length > 0 && (
          <div className="rounded border-l-4 border-warn-fg bg-warn px-3 py-2">
            <p className="text-xs font-semibold text-warn-fg">Not everything could be checked</p>
            <ul className="mt-1 space-y-0.5 text-xs text-warn-fg/90">
              {allSkipped.map((s) => <li key={s}>{s}</li>)}
            </ul>
          </div>
        )}

        {!loading && data?.status === 'resolved'
          && !data.ediValidated && data.ediSkippedReason && (
          <p className="text-xs text-muted">
            EDI validation did not run: {data.ediSkippedReason}.
          </p>
        )}

        {/* --- the orders --- */}
        {!loading && data?.status === 'resolved' && data.rows.length === 0 && (
          <div className="rounded-lg border border-rule bg-panel px-4 py-6 text-sm text-body">
            No orders are attached to this invoice.
          </div>
        )}

        {!loading && data?.status === 'resolved' && target
          && data.rows.map((row, i) => (
            <ProBlock
              key={`${row.pronumber}-${row.invoicerecno}`}
              row={row}
              index={i}
              count={data.rows.length}
              sageTag={target.mas90code}
              billtoname={target.billtoname}
              billto={target.billto}
              credithold={target.credithold}
              margin={marginByPro.get(row.pronumber)}
              marginPending={!!marginPending}
              // allHolds, NOT data.holds: MISSING DRIVER RATES and MARGIN APPROVAL
              // arrive later from the batched margin fetch, and reading the unmerged
              // list would leave them off the block for exactly the pros they name.
              holds={allHolds.filter((h) => h.pronumbers.includes(row.pronumber))}
              onOpenPro={setDrawerPro}
              onOpenMargin={setMarginPro}
              onOpenAccessorial={(pro, recno) => setAccEdit({ pro, recno })}
              onOpenQuote={(quoteNumber, revisionNumber) => setQuoteRef({ quoteNumber, revisionNumber })}
              onOpenEdi={setEdiPro}
              removable={canRemoveRows && row.accessorialnum !== 0}
              excluded={excluded.has(row.invoicerecno)}
              onToggleExcluded={() => toggleExcluded(row.invoicerecno)}
              pageRecnos={pageRecnos}
            />
          ))}

      </div>

      {/* Mounted, not conditionally rendered — same as the EDI screen, so the drawer keeps
          its width and Master Pro navigation across opens. Reloading on close means an
          edit made in there lands here immediately. */}
      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={() => { setDrawerPro(null); void load(); }}
      />

      {marginPro !== null && (
        <MarginBreakdown pro={marginPro} onClose={() => setMarginPro(null)} />
      )}

      {/* The order's own accessorial modal, mounted here unchanged. Its read-only gate
          is driven by the RECORD (`invoiced`), not by who opened it, so an invoiced
          accessorial locks here exactly as it does on the Invoicing tab.

          Deliberately NOT wrapped in an AdminOverrideProvider. Override is per-pro and
          re-entered on purpose every time (see AdminOverrideContext) — the context's
          no-provider default is "off and unavailable", which is the state a user sees on
          the pro before they turn it on. An admin who needs to unlock an invoiced
          accessorial does it from the pro, where the toggle lives. */}
      {accEdit && (
        <AccessorialEditModal
          open
          pronumber={accEdit.pro}
          recno={accEdit.recno}
          onClose={() => setAccEdit(null)}
          onSaved={() => { setAccEdit(null); void load(); }}
        />
      )}

      {/* The quote drawer, opened from the Quote line above each charge table. The
          drawer takes a lane row because that is what the lane table hands it, but it
          loads the quote from the number/revision pair alone — this is the same minimal
          lane the order's Invoicing tab builds for the same drawer
          (InvoicingView.tsx:1794).

          No `onChanged`: an edit made in there changes the QUOTE, not this invoice. The
          charges below were copied onto the invoice when it was built and do not follow
          the quote, so re-fetching the page would redraw identical rows and lose the
          scroll position. */}
      <QuoteLaneEditorDrawer
        open={quoteRef !== null}
        lane={quoteRef ? ({
          quoteId: `${quoteRef.quoteNumber}-${quoteRef.revisionNumber}`,
          quoteNumber: quoteRef.quoteNumber,
          revisionNumber: quoteRef.revisionNumber,
          quoteType: 'DIRECT',
          customer: '', billTo: '', lane: '', csr: '', effective: '', expires: '',
          pros: 0, status: 'UNSECURED', hot: false,
        } as QuoteListRow) : null}
        onClose={() => setQuoteRef(null)}
      />

      {/* The pro's own EDI Info modal, mounted here unchanged. It fetches by pronumber
          and portals to <body>, so it needs nothing from this page but the pro. */}
      <EdiInfoModal
        open={ediPro !== null}
        pronumber={ediPro ?? undefined}
        onClose={() => setEdiPro(null)}
      />

      {options && target && (
        <InvoiceOptionsDialog
          mode={options}
          billto={target.billto}
          invoiceClass={invoiceClass}
          title={options.kind === 'create'
            ? `Create invoice — ${target.billtoname}`
            : options.kind === 'estimate'
              ? `Invoice estimate — ${target.billtoname}`
              : `Invoice ${target.invoicenumber}`}
          // The options dialog can save the PDF; only this page knows the invoice NUMBER
          // to name it after (the options payload carries the history recno, not the
          // number), so it supplies the filename.
          // No "invoice-" prefix on a real invoice: the clerks asked for the number
          // exactly as they say it, "EASTCHEM-3146035", and it is the same string the
          // archived PDF is now named with (creation.invoice_pdf_filename). An ESTIMATE
          // keeps a prefix because its number is a masterpro, which on its own would
          // read as an invoice that had been raised.
          pdfName={options.kind === 'estimate'
            ? `invoice-estimate-${target.masterpro || target.billto}`
            : target.invoicenumber || undefined}
          onClose={() => {
            setOptions(null);
            createRequestId.current = null;
            emailBatch.current = null;
            heldItems.current = [];
            createdInvoices.current = [];
          }}
          // Only a creation changes what this page is showing. Viewing, printing,
          // e-mailing and estimating leave the invoice exactly as it was, so they close
          // quietly and say what happened on the page instead of reloading it.
          onDone={({ note, created, recnos }) => {
            setOptions(null);
            createRequestId.current = null;
            const queued = emailBatch.current;
            const held = heldItems.current;
            const createdList = createdInvoices.current;
            emailBatch.current = null;
            heldItems.current = [];
            createdInvoices.current = [];
            if (!created) {
              if (note) setPageNote({ tone: 'ok', text: note });
              return;
            }
            // "Sending" and not "sent": the batch is queued and the messages leave over
            // the following minutes. Claiming they have gone would be the one sentence a
            // user would rely on and could not check.
            const sending = queued
              ? ` Sending ${queued.invoices} ${queued.invoices === 1 ? 'e-mail' : 'e-mails'} to the Bill To.`
              : '';
            const raised = recnos.length === 1
              ? `Invoice created.${sending}${note ? ` ${note}` : ''}`
              : `${recnos.length} invoices created.${sending}${note ? ` ${note}` : ''}`;
            const heldNote = held.length
              ? ` ${held.length} ${held.length === 1 ? 'set of charges was' : 'sets of charges were'} held and not invoiced.`
              : '';
            // The lists ride separately so they can be rendered as lists — a run that
            // raised thirteen invoices and held two says nothing useful crammed into one
            // line, and the invoice numbers need to be clickable.
            setPageNote({
              tone: heldNote ? 'bad' : 'ok',
              text: raised + heldNote,
              created: createdList.length > 1 ? createdList : undefined,
              held: held.length ? held : undefined,
            });
            keepNoteOnce.current = true;
            // ONE invoice: go to it. The page was opened on a masterpro that no longer
            // has uninvoiced charges, so leaving the URL alone would render the invoice
            // it just raised as "No orders are attached to this invoice".
            if (recnos.length === 1) {
              onSelect({ invhistoryrecno: recnos[0] });
              return;
            }
            // SEVERAL: a per-pro split produced one invoice per pro, and picking the
            // first of five to show is picking arbitrarily. Reload the filters the page
            // already has — the same masterpro now resolves to the picker listing every
            // invoice the run created, which is the screen that actually answers "what
            // did that just do?".
            void load();
          }}
          onConfirm={async (choice) => {
            if (options.kind === 'existing') return [options.invoiceHistoryRecno];
            // An estimate creates nothing, so there is nothing to confirm and no invoice
            // history to hand back. The dialog renders the PDF itself. Same for a credit
            // memo, which this page never opens — the narrowing is what TypeScript needs
            // to reach `invoiceRecnos` below.
            if (options.kind !== 'create') return [];
            // Review under the server's eyes first: it owns the holds verdict and the
            // snapshot the write is checked against.
            const review = await previewInvoiceCreation(options.invoiceRecnos, invoiceClass);
            if (!review.canCreate) {
              throw new Error([...review.holds, ...review.skipped].join(' ') || 'This invoice cannot be created yet.');
            }
            // One UUID for the whole attempt: a retry after a lost response must not
            // create a second invoice.
            createRequestId.current ??= crypto.randomUUID();
            const result = await createInvoices({
              invoiceRecnos: options.invoiceRecnos, invoiceClass: review.invoiceClass,
              snapshot: review.snapshot, requestId: createRequestId.current,
              emailInvoices: choice.emailInvoices,
            });
            emailBatch.current = result.emailBatch
              ? { batch: result.emailBatch, invoices: result.invoices.length }
              : null;
            heldItems.current = result.heldItems ?? [];
            createdInvoices.current = result.invoices.map(
              (i) => ({ recno: i.recno, invoiceNumber: i.invoiceNumber }),
            );
            return result.invoices.map((i) => i.recno);
          }}
        />
      )}

      {cancelling && target && (
        <CancelInvoiceDialog
          invhistoryrecno={target.invhistoryrecno}
          invoicenumber={target.invoicenumber}
          proCount={data?.pronumbers.length ?? 0}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false);
            // Reload rather than patch: the page now shows the CANCELLED badge, the holds
            // are re-evaluated against uninvoiced charges, and the totals come from the
            // same query everything else on this screen reads.
            void load();
          }}
        />
      )}
    </div>
  );
};

const InvoiceList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // The filters ARE the URL. Reading them here rather than holding them in state means a
  // link, a back button and a picker click all take the same path.
  const params = useMemo<InvoiceListParams>(() => {
    const p: InvoiceListParams = {};
    const num = (k: 'invhistoryrecno' | 'billto') => {
      const v = searchParams.get(k);
      if (v && !Number.isNaN(Number(v))) p[k] = Number(v);
    };
    num('invhistoryrecno');
    num('billto');
    (['masterpro', 'billtoname', 'invoicenum', 'invoicerecnos',
      'invoicestartdate', 'invoiceenddate'] as const).forEach((k) => {
      const v = searchParams.get(k);
      if (v) p[k] = v;
    });
    return p;
  }, [searchParams]);

  return <InvoiceListContent params={params} onSelect={selected => navigate(invoiceListPath(selected))} />;
};

export default InvoiceList;
